import Button from "@/common/elements/Button";
import Table from "@/common/elements/Table";
import { useEffect, useState } from "react";
import EditChargesModal from "./EditChargesModal";
import { ChargesListContainer } from "./styles";
import dayjs from "dayjs";
import {
  checkGlobalClientParams,
  getDayjsObject,
  getRefListItemsbyName,
  getRefListNameByID,
  hasCheckSecurity,
  isNonEmptyVariable,
} from "@/util/common_function";
import { useDispatch, useSelector } from "react-redux";
import { setBookings, setDeleteAPis } from "@/slices/bookingsSlice";
import { Popconfirm } from "antd";
import { TableActionsHeader } from "@/common/styles";
import _ from "lodash";
import api from "@/services/axios_custom";
import API_ENDPOINTS from "@/util/api";
import {
  Arbitrary_Names,
  Entity_types,
  Security_Types,
} from "@/util/permission_bits";
import { LABELS_CAPITAL } from "@/util/usePermissionMask";
import {
  MODULES_LIST_WITH_ID,
  SUB_MODULES_LIST_WITH_ID,
} from "@/util/common_variable";
import {
  normalizeKey,
  pickStickyFields,
  useStickyFieldNames,
} from "@/hooks/useStickyFields";
import useDialog from "@/hooks/useDialog";
import useGridColumnConfig from "@/hooks/useGridColumnConfig";

const CHARGES_HEADERS = [
  { key: "sequence", text: "ID" },
  { key: "chargeDescription", text: "Charge" },
  { key: "courtNumber", text: "Court File Number" },
  { key: "chargeCount", text: "CNT" },
  { key: "scheduledReleaseDate", text: "Release Date" },
  { key: "controllingAgencyORI", text: "Controlling Agency" },
  { key: "controllingCharge1", text: "Controlling Charge " },
  { key: "sentence", text: "Sentence" },
  { key: "sentenceConsecutive", text: "Consecutive" },
  { key: "clearedToGo", text: "Cleared" },
  { key: "requiresBond", text: "Bail" },
  { key: "bookingBailID", text: "Bail ID" },
  { key: "sentenced", text: "Sentenced" },
  { key: "sealed", text: "Sealed" },
];

const ChargesList = ({
  chargesData,
  switchToAddCharges,
  bookingBails,
  startingPoint,
}) => {
  const dispatch = useDispatch();
  const { bookings, bookingsFieldsEnabled } = useSelector(
    (state) => state?.bookings,
  );

  // "Config" — drag-reorder/resize columns, persisted per-user (see
  // docs/plans/grid-column-config-feature.md).
  const {
    headers: chargesHeaders,
    isConfiguring,
    configButtonLabel,
    startConfiguring,
    confirmSave,
    discardChanges,
    dataTableRef,
  } = useGridColumnConfig("Bookings:Charges", CHARGES_HEADERS);

  const [isModalShown, setIsModalShown] = useState(false);
  const [tempChargesData, setTempChargesData] = useState([]);
  const [selectChargeData, setSelectChargeData] = useState(null);
  const [editShow, setEditShow] = useState(false);
  // Sticky-field defaults for a new charge being created off the selected
  // charge below. Kept separate from selectChargeData/formData so the save
  // logic (EditChargesModal) still treats this as a brand-new charge rather
  // than an edit of the row it was copied from.
  const [stickyDefaults, setStickyDefaults] = useState(null);
  const chargeStickyFieldNames = useStickyFieldNames("Charges");
  const { showDialog } = useDialog();
  // "Add Charges Based on Selected" only makes sense when sticky fields are
  // turned on for Charges (ClientParams row: ParamName="Charges",
  // ParamContext="UseStickyFields", ParamValue="True").
  const showAddChargesBasedOnSelected = !!checkGlobalClientParams(
    "Charges",
    "UseStickyFields",
    "True",
  );

  useEffect(() => {
    console.log(chargesData);

    setTempChargesData(() => chargesData);
  }, [chargesData]);

  const onModalHide = () => {
    setIsModalShown(false);
    cancelAction();
    setEditShow(false);
    setStickyDefaults(null);
  };

  const onModalShow = () => {
    setIsModalShown(true);
  };

  const selectChargeFn = (data) => {
    const values = chargesData?.find((c) => c?.uuid == data?.uuid);
    setSelectChargeData(() => ({
      ...values,
      offenseDateTime:
        isNonEmptyVariable(values?.offenseDate) &&
        isNonEmptyVariable(values?.offenseTime)
          ? dayjs(values?.offenseDate + " " + values?.offenseTime)
          : null,
      dispositionDate: isNonEmptyVariable(values?.dispositionDate)
        ? dayjs(values?.dispositionDate)
        : null,
      clearanceDateTime:
        isNonEmptyVariable(values?.clearanceDate) &&
        isNonEmptyVariable(values?.clearanceTime)
          ? dayjs(values?.clearanceDate + " " + values?.clearanceTime)
          : null,
      sentenceDate: isNonEmptyVariable(values?.sentenceDate)
        ? dayjs(values?.sentenceDate)
        : null,

      sentenceStartDateTime:
        isNonEmptyVariable(values?.sentenceStartDate) &&
        isNonEmptyVariable(values?.sentenceStartTime)
          ? dayjs(values?.sentenceStartDate + " " + values?.sentenceStartTime)
          : null,

      sentenceEndDate: isNonEmptyVariable(values?.sentenceEndDate)
        ? dayjs(values?.sentenceEndDate)
        : null,

      scheduledReleaseDateTime:
        isNonEmptyVariable(values?.scheduledReleaseDate) &&
        isNonEmptyVariable(values?.scheduledReleaseTime)
          ? dayjs(
              values?.scheduledReleaseDate + " " + values?.scheduledReleaseTime,
            )
          : null,
      deadlineDateTime1Local: isNonEmptyVariable(values?.deadlineDateTime1)
        ? dayjs(values?.deadlineDateTime1)
        : null,
      deadlineDateTime2Local: isNonEmptyVariable(values?.deadlineDateTime2)
        ? dayjs(values?.deadlineDateTime2)
        : null,

      deadlineStartDateTime:
        isNonEmptyVariable(values?.deadlineStartDate) &&
        isNonEmptyVariable(values?.deadlineStartDate)
          ? dayjs(values?.deadlineStartDate + " " + values?.deadlineStartTime)
          : null,

      deadlineSatisfiedDateTime1: getDayjsObject(
        values?.deadlineSatisfiedDate1,
        values?.deadlineSatisfiedTime1,
      ),
      deadlineSatisfiedDateTime2: getDayjsObject(
        values?.deadlineSatisfiedDate2,
        values?.deadlineSatisfiedTime2,
      ),
    }));
    setEditShow(true);
  };

  const addCharge = () => {
    setSelectChargeData(null);
    setStickyDefaults(null);
    onModalShow();
  };

  // pickStickyFields does a flat copy of the source record's own top-level
  // keys. Two kinds of sticky fields need extra handling before the result
  // is usable by AddCharges' form:
  //   - a *_Date field (e.g. Offense_Date) is stored raw (a date string),
  //     but the form binds to a combined *DateTime dayjs field instead
  //     (e.g. offenseDateTime) — see selectChargeFn above for the same
  //     combination applied when opening a charge for edit.
  //   - a field that lives on the nested Charges.court[] array (e.g.
  //     Court_Name_ID) has no top-level counterpart at all, so the flat
  //     copy silently skips it (see useStickyFields.js).
  // Both are handled below so "Add Charge Based on Selected" can actually
  // carry these fields forward instead of leaving them blank.

  // Raw *_Date/*_Time pairs -> the combined dayjs *DateTime field the
  // AddCharges form actually binds to. Falls back to the date alone (via
  // getDayjsObject) when only the date half was picked as sticky, since
  // Offense_Time etc. isn't necessarily configured sticky too.
  const buildStickyDateTimeFields = (picked) => ({
    offenseDateTime: getDayjsObject(picked?.offenseDate, picked?.offenseTime),
    dispositionDate: isNonEmptyVariable(picked?.dispositionDate)
      ? dayjs(picked?.dispositionDate)
      : null,
    clearanceDateTime: getDayjsObject(
      picked?.clearanceDate,
      picked?.clearanceTime,
    ),
    sentenceDate: isNonEmptyVariable(picked?.sentenceDate)
      ? dayjs(picked?.sentenceDate)
      : null,
    sentenceStartDateTime: getDayjsObject(
      picked?.sentenceStartDate,
      picked?.sentenceStartTime,
    ),
    sentenceEndDate: isNonEmptyVariable(picked?.sentenceEndDate)
      ? dayjs(picked?.sentenceEndDate)
      : null,
    scheduledReleaseDateTime: getDayjsObject(
      picked?.scheduledReleaseDate,
      picked?.scheduledReleaseTime,
    ),
  });

  // Mirrors PCI's "Add Charge Based on Selected" (BookingsInterface.vbs/
  // AddStickyCharge): copy only the fields marked sticky for the Charges
  // AppContext from the currently selected charge onto a brand-new charge,
  // then open the Add form pre-filled with just those values.
  const addChargeBasedOnSelected = () => {
    if (!editShow) {
      showDialog("info", {
        title: "No Charge Selected",
        content: "Please select a charge from the list.",
        confirmButtonText: "OK",
      });
      return;
    }
    const defaults = pickStickyFields(selectChargeData, chargeStickyFieldNames);
    // "SINGLE" per legacy's ChargesCourt[@SINGLE] field source: take the
    // one court entry that's relevant to a sticky default rather than the
    // full court-date history. There's no "current court" flag on the
    // record, so this uses the first entry as that single representative.
    const stickyCourtDefaults = pickStickyFields(
      selectChargeData?.court?.[0],
      chargeStickyFieldNames,
    );
    // "Inmate TrackingID" doesn't live flatly on the court entry — it's
    // the ID of that entry's own inmateTracking[0] movement/transport
    // record (court[].inmateTracking[0].inmateTrackingID), so the generic
    // flat pickStickyFields above can't reach it. It also isn't safe to
    // copy as a bare ID: that would point the new (otherwise blank) court
    // row at an existing tracking record without its date/time/status,
    // risking that record getting overwritten on save. Instead, when this
    // field is configured sticky, carry the whole inmateTracking
    // sub-object forward so the new charge's court entry shares the same
    // movement/transport as the selected charge's, exactly as legacy PCI
    // intended (multiple charges added for the same court appearance
    // track as one movement instead of spawning a duplicate).
    //
    // Per business rule, a sticky Inmate TrackingID also carries the court
    // date/time and disposition along with it — independently of whether
    // Court_Name_ID is itself configured sticky — since those describe the
    // same shared court appearance the tracking record represents.
    const isInmateTrackingIDSticky = chargeStickyFieldNames.some(
      (name) => normalizeKey(name) === "inmatetrackingid",
    );
    if (isInmateTrackingIDSticky) {
      const sourceCourt = selectChargeData?.court?.[0];
      if (isNonEmptyVariable(sourceCourt?.inmateTracking?.[0])) {
        stickyCourtDefaults.inmateTracking = [sourceCourt.inmateTracking[0]];
      }
      if (isNonEmptyVariable(sourceCourt?.courtDate)) {
        stickyCourtDefaults.courtDate = sourceCourt.courtDate;
      }
      if (isNonEmptyVariable(sourceCourt?.courtTime)) {
        stickyCourtDefaults.courtTime = sourceCourt.courtTime;
      }
      if (isNonEmptyVariable(sourceCourt?.offenderDispositionID)) {
        stickyCourtDefaults.offenderDispositionID =
          sourceCourt.offenderDispositionID;
      }
    }
    setStickyDefaults({
      ...defaults,
      ...buildStickyDateTimeFields(defaults),
      ...(Object.keys(stickyCourtDefaults).length
        ? { court: [stickyCourtDefaults] }
        : {}),
    });
    setSelectChargeData(null);
    onModalShow();
  };

  const editCharge = () => {
    onModalShow();
  };

  const deleteCharge = () => {
    let data = _.cloneDeep(chargesData);
    data = data?.filter((d) => {
      return d?.uuid !== selectChargeData?.uuid;
    });

    dispatch(
      setBookings({
        charges: data,
      }),
    );
    const url = API_ENDPOINTS.charges + "/id/" + selectChargeData?.chargeID;
    dispatch(setDeleteAPis(url));
    cancelAction();
  };

  const cancelAction = () => {
    setSelectChargeData(() => null);
    setEditShow(false);
  };

  return (
    <>
      <ChargesListContainer>
        <TableActionsHeader className="action-header-row">
          <div className="left">
            {startingPoint != "FolderSearch" && (
              <Button
                style={{ marginRight: "10px" }}
                color="primary"
                type="primary"
                onClick={addCharge}
                disabled={
                  !bookingsFieldsEnabled ||
                  isConfiguring ||
                  !hasCheckSecurity(
                    Security_Types.functionality,
                    Entity_types.subModule,
                    LABELS_CAPITAL.ADD,
                    MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
                    SUB_MODULES_LIST_WITH_ID.Arrest_Report,
                  )
                }
              >
                Add Charges
              </Button>
            )}
            {startingPoint != "FolderSearch" && showAddChargesBasedOnSelected && (
              <Button
                color="primary"
                type="primary"
                onClick={addChargeBasedOnSelected}
                disabled={
                  !bookingsFieldsEnabled ||
                  isConfiguring ||
                  !hasCheckSecurity(
                    Security_Types.functionality,
                    Entity_types.subModule,
                    LABELS_CAPITAL.ADD,
                    MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
                    SUB_MODULES_LIST_WITH_ID.Arrest_Report,
                  )
                }
              >
                Add Charges Based on Selected
              </Button>
            )}
          </div>
          <div className="right">
            {
              <Button
                color="primary"
                type="primary"
                onClick={editCharge}
                disabled={
                  !editShow ||
                  isConfiguring ||
                  !hasCheckSecurity(
                    Security_Types.functionality,
                    Entity_types.subModule,
                    LABELS_CAPITAL.VIEW,
                    MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
                    SUB_MODULES_LIST_WITH_ID.Arrest_Report,
                  )
                }
              >
                Details
              </Button>
            }
            {hasCheckSecurity(
              Security_Types.functionality,
              Entity_types.arbitary,
              LABELS_CAPITAL.MODIFY,
              Arbitrary_Names.EnableDeleteCharges,
            ) &&
              startingPoint != "FolderSearch" && (
                <Popconfirm
                  title="Delete the charges"
                  description="Are you sure to delete this charges?"
                  onConfirm={deleteCharge}
                  onCancel={() => {}}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button
                    color="primary"
                    type="primary"
                    disabled={
                      !editShow ||
                      !bookingsFieldsEnabled ||
                      isConfiguring ||
                      !hasCheckSecurity(
                        Security_Types.functionality,
                        Entity_types.subModule,
                        LABELS_CAPITAL.DELETE,
                        MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
                        SUB_MODULES_LIST_WITH_ID.Arrest_Report,
                      )
                    }
                  >
                    DELETE
                  </Button>
                </Popconfirm>
              )}

            <Button
              color="primary"
              type="primary"
              onClick={cancelAction}
              disabled={!editShow || isConfiguring}
            >
              Cancel
            </Button>
            {/* <Button
              color="primary"
              type="primary"
              disabled={!bookingsFieldsEnabled}
            >
              Live Scan
            </Button> */}

            {/* Config — drag-reorder/resize columns; only while Modify is active */}
            {!isConfiguring ? (
              <Button
                color="primary"
                type="primary"
                htmlType="button"
                onClick={startConfiguring}
              >
                {configButtonLabel}
              </Button>
            ) : (
              <Popconfirm
                title="Do you want to save the current list view configuration?"
                onConfirm={confirmSave}
                onCancel={discardChanges}
                okText="Yes"
                cancelText="No"
              >
                <Button
                  color="primary"
                  type="primary"
                  htmlType="button"
                >
                  {configButtonLabel}
                </Button>
              </Popconfirm>
            )}
          </div>
        </TableActionsHeader>
        {hasCheckSecurity(
          Security_Types.functionality,
          Entity_types.subModule,
          LABELS_CAPITAL.VIEW,
          MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
          SUB_MODULES_LIST_WITH_ID.Arrest_Report,
        ) && (
          <Table
            ref={dataTableRef}
            headers={chargesHeaders}
            draggable={isConfiguring}
            data={tempChargesData?.map((td, index) => {
              return {
                ...td,
                chargeDescription: getRefListNameByID(
                  "Charges",
                  td?.chargeDescriptionID,
                ),
                controllingAgencyORI: getRefListNameByID(
                  "Controlling_Agency",
                  td?.controllingAgencyORIID,
                  false,
                  true,
                ),
                sequence: td?.sequence || index + 1,
                requiresBond: td?.requiresBond ? "True" : "False",
                clearedToGo: td?.clearedToGo ? "True" : "False",
                sentenced: td?.sentenced ? "True" : "False",
                sentenceConsecutive: td?.sentenceConsecutive ? "True" : "False",
                sealed: td?.sealed ? "True" : "False",
                controllingCharge1: td?.controllingCharge ? "True" : "False",
              };
            })}
            onRowClick={selectChargeFn}
            onRowDoubleClick={(value) => {
              selectChargeFn(value);
              editCharge();
            }}
            selected={selectChargeData === null}
          />
        )}
      </ChargesListContainer>
      {isModalShown && (
        <EditChargesModal
          show={isModalShown}
          isEdit={editShow && bookingsFieldsEnabled}
          selectChargeData={selectChargeData}
          stickyDefaults={stickyDefaults}
          onHide={onModalHide}
          chargesData={chargesData}
          bookingBails={bookingBails}
          startingPoint={startingPoint}
        />
      )}
    </>
  );
};

export default ChargesList;
